import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, Download, Network, Pencil, Save, X, Plus, Trash2, UserRound } from "lucide-react";
import { downloadElementAsPdf } from "@/lib/exportPdf";

const LEVEL_COLORS = [
  "bg-violet-100 border-violet-300 text-violet-900",
  "bg-teal-100 border-teal-300 text-teal-900",
  "bg-pink-100 border-pink-300 text-pink-900",
  "bg-yellow-100 border-yellow-300 text-yellow-900",
  "bg-orange-100 border-orange-300 text-orange-900",
  "bg-sky-100 border-sky-300 text-sky-900",
];

const uid = () => `b-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const MAX_PHOTO_BYTES = 3 * 1024 * 1024;

const readImageFile = (file) => new Promise((resolve, reject) => {
  if (!file.type.startsWith("image/")) {
    reject(new Error("Choose an image file"));
    return;
  }
  if (file.size > MAX_PHOTO_BYTES) {
    reject(new Error("Photo must not exceed 3 MB"));
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("Unable to read this photo"));
  reader.readAsDataURL(file);
});

export default function OrgStructureChart() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.staff_role === "Hostel Supervisor";
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get("/org-structure");
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load org chart");
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = () => {
    setDraft({ title: data.title, boxes: data.boxes.map((b) => ({ ...b })) });
    setEditing(true);
  };
  const cancel = () => { setDraft(null); setEditing(false); };

  const setField = (id, field, value) =>
    setDraft((d) => ({ ...d, boxes: d.boxes.map((b) => (b.id === id ? { ...b, [field]: value } : b)) }));
  const selectPhoto = async (id, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      setField(id, "photo", await readImageFile(file));
    } catch (error) {
      toast.error(error.message);
    }
  };
  const removeBox = (id) => setDraft((d) => ({ ...d, boxes: d.boxes.filter((b) => b.id !== id) }));
  const addBox = (level) => setDraft((d) => ({ ...d, boxes: [...d.boxes, { id: uid(), level, role: "Jawatan", name: "Nama" }] }));
  const addRow = () => {
    const max = draft.boxes.reduce((m, b) => Math.max(m, b.level), -1);
    addBox(max + 1);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/org-structure", {
        title: draft.title,
        boxes: draft.boxes.map((b) => ({ id: b.id, level: b.level, role: b.role, name: b.name })),
      });
      for (const box of draft.boxes) {
        const previous = data.boxes.find((item) => item.id === box.id)?.photo || null;
        const next = box.photo || null;
        if (previous === next) continue;
        if (next) {
          await api.put(`/org-structure/${encodeURIComponent(box.id)}/photo`, { profile_picture: next });
        } else {
          await api.delete(`/org-structure/${encodeURIComponent(box.id)}/photo`);
        }
      }
      const refreshed = await api.get("/org-structure");
      setData(refreshed.data);
      setDraft(null);
      setEditing(false);
      toast.success("Org chart updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save org chart");
    } finally {
      setSaving(false);
    }
  };

  const exportChart = async () => {
    setExporting(true);
    try {
      const title = view.title || "Org Chart";
      const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "org-chart";
      await downloadElementAsPdf(exportRef.current, {
        filename: `${slug}.pdf`,
        title,
      });
      toast.success("Org chart PDF downloaded");
    } catch (error) {
      toast.error("Unable to export the org chart PDF");
    } finally {
      setExporting(false);
    }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const view = editing ? draft : data;
  const maxLevel = view.boxes.reduce((m, b) => Math.max(m, b.level), 0);
  const levels = Array.from({ length: maxLevel + 1 }, (_, lvl) => view.boxes.filter((b) => b.level === lvl));

  return (
    <section id="org-structure-export" ref={exportRef} className="space-y-5 border-t-4 border-indigo-500 pt-4" data-testid="org-structure-section" aria-labelledby="org-structure-title">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary">
            <Network className="h-4 w-4" /> Org Chart
          </p>
          {editing ? (
            <Input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} className="mt-2 font-display font-bold" data-testid="org-structure-title-input" />
          ) : (
            <h2 id="org-structure-title" className="mt-1 font-display text-xl font-bold" data-testid="org-structure-title">{data.title}</h2>
          )}
        </div>
        <div className="flex gap-2" data-pdf-exclude>
          <Button variant="outline" onClick={exportChart} disabled={exporting || editing} title={editing ? "Save or cancel edits before exporting" : "Export the org chart as PDF"} data-testid="org-structure-export-pdf-btn"><Download className="h-4 w-4" /> {exporting ? "Generating PDF…" : "Export PDF"}</Button>
          {canEdit && (editing ? (
            <>
              <Button variant="outline" onClick={cancel} data-testid="org-structure-cancel-btn"><X className="h-4 w-4" /> Batal</Button>
              <Button onClick={save} disabled={saving} data-testid="org-structure-save-btn"><Save className="h-4 w-4" /> {saving ? "Menyimpan…" : "Simpan"}</Button>
            </>
          ) : (
            <Button variant="outline" onClick={startEdit} data-testid="org-structure-edit-btn"><Pencil className="h-4 w-4" /> Edit carta</Button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[640px] space-y-1">
          {levels.map((boxes, lvl) => (
            <div key={lvl}>
              {lvl > 0 && <div className="mx-auto h-4 w-px bg-border" aria-hidden="true" />}
              <div className="flex flex-wrap items-stretch justify-center gap-3" data-testid={`org-level-${lvl}`}>
                {boxes.map((b) => (
                  <div
                    key={b.id}
                    data-testid={`org-box-${b.id}`}
                    className={`relative w-52 rounded-xl border px-3 py-2 text-center shadow-sm ${LEVEL_COLORS[lvl % LEVEL_COLORS.length]}`}
                  >
                    {editing ? (
                      <div className="space-y-1.5">
                        <div className="relative mx-auto flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-current/20 bg-white/70">
                          {b.photo ? <img src={b.photo} alt={`${b.name} portrait`} className="h-full w-full object-cover" /> : <UserRound className="h-8 w-8 opacity-45" />}
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <label htmlFor={`org-box-photo-${b.id}`} className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium hover:underline">
                            <Camera className="h-3.5 w-3.5" /> {b.photo ? "Ganti foto" : "Tambah foto"}
                          </label>
                          <input id={`org-box-photo-${b.id}`} type="file" accept="image/*" className="sr-only" onChange={(event) => selectPhoto(b.id, event)} />
                          {b.photo && <button type="button" onClick={() => setField(b.id, "photo", null)} className="text-xs text-destructive hover:underline" aria-label="Buang foto">Buang</button>}
                        </div>
                        <Input value={b.role} onChange={(e) => setField(b.id, "role", e.target.value)} className="h-8 text-center text-xs font-bold uppercase" aria-label="Jawatan" data-testid={`org-box-role-${b.id}`} />
                        <Input value={b.name} onChange={(e) => setField(b.id, "name", e.target.value)} className="h-8 text-center text-sm" aria-label="Nama" data-testid={`org-box-name-${b.id}`} />
                        <button onClick={() => removeBox(b.id)} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow" aria-label="Buang kotak" data-testid={`org-box-remove-${b.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-current/20 bg-white/70">
                          {b.photo ? <img src={b.photo} alt={`${b.name} portrait`} className="h-full w-full object-cover" /> : <UserRound className="h-8 w-8 opacity-45" />}
                        </div>
                        <p className="text-xs font-bold uppercase tracking-wide">{b.role}</p>
                        <p className="mt-0.5 text-sm font-medium leading-tight">{b.name}</p>
                      </>
                    )}
                  </div>
                ))}
                {editing && (
                  <button onClick={() => addBox(lvl)} className="flex w-52 items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary" data-testid={`org-add-box-${lvl}`}>
                    <Plus className="h-4 w-4" /> Tambah kotak
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <Button variant="outline" size="sm" onClick={addRow} data-testid="org-add-row-btn"><Plus className="h-4 w-4" /> Tambah baris baharu</Button>
      )}
      {canEdit && !editing && (
        <p className="text-xs text-muted-foreground" data-pdf-exclude>Admins and Hostel Supervisors can edit roles and names, and add or remove boxes when wardens change.</p>
      )}
    </section>
  );
}
